import type {Editor} from 'grapesjs';
import {DEFAULT_ZOOM, ZOOM_LOWER_LIMIT, ZOOM_PERCENTAGE_MULTIPLIER, ZOOM_STEP, ZOOM_UPPER_LIMIT} from '@EmailMakerReact/email-maker-react.constants';
import {fixLoadingOverlayForEmailSize} from '@EmailMakerReact/lib/email-maker-overlay-library';

/**
 * Canvas zoom for the React email maker.
 * Per-editor zoom state is stored on the GrapesJS model and read via `getCurrentZoom`.
 */

const ZOOM_STATE_MODEL_KEY = 'pmwEmailMakerZoomState';

interface EmailMakerZoomState {
  zoomValue: number;
}

/** The current canvas zoom factor; DEFAULT_ZOOM until the first zoom action. */
export function getCurrentZoom(editor: Editor): number {
  const state = editor.getModel().get(ZOOM_STATE_MODEL_KEY) as EmailMakerZoomState | undefined;
  return state?.zoomValue ?? DEFAULT_ZOOM;
}

/** Subscribes to zoom-state changes on the editor model; returns an unsubscribe. */
export function subscribeToZoomChange(editor: Editor, callback: () => void): () => void {
  const event = `change:${ZOOM_STATE_MODEL_KEY}`;
  editor.getModel().on(event, callback);
  return (): void => {
    editor.getModel().off(event, callback);
  };
}

/** Scales the canvas body up by one step, clamped to ZOOM_UPPER_LIMIT. */
export function zoomIn(editor: Editor): void {
  applyZoom(editor, Math.min(getCurrentZoom(editor) + ZOOM_STEP, ZOOM_UPPER_LIMIT));
}

/** Scales the canvas body down by one step, clamped to ZOOM_LOWER_LIMIT. */
export function zoomOut(editor: Editor): void {
  applyZoom(editor, Math.max(getCurrentZoom(editor) - ZOOM_STEP, ZOOM_LOWER_LIMIT));
}

/** Resets the canvas zoom to the default. */
export function fitToScreen(editor: Editor): void {
  const editorBody = editor.Canvas.getBody();
  if (!editorBody) {
    return;
  }

  editorBody.style.transform = `scale(${DEFAULT_ZOOM})`;
  setCurrentZoom(editor, DEFAULT_ZOOM);
  editorBody.style.height = '100%';

  fixLoadingOverlayForEmailSize(editor);
}

function setCurrentZoom(editor: Editor, zoomValue: number): void {
  const state: EmailMakerZoomState = {zoomValue};
  editor.getModel().set(ZOOM_STATE_MODEL_KEY, state);
}

function applyZoom(editor: Editor, zoomValue: number): void {
  const editorBody = editor.Canvas.getBody();
  if (!editorBody) {
    return;
  }

  setCurrentZoom(editor, zoomValue);
  editorBody.style.transform = `scale(${zoomValue})`;

  if (zoomValue <= DEFAULT_ZOOM) {
    editorBody.style.height = `${zoomValue * ZOOM_PERCENTAGE_MULTIPLIER}%`;
  }

  fixLoadingOverlayForEmailSize(editor);
}
